// Copyright (c) 2014 Rafael Caricio. All rights reserved.
// Use of this source code is governed by a BSD-style license that can be
// found in the LICENSE file.

export const GradientParser = {};

export const gradientColors = [
  'linear-gradient(79deg, #2b0aff, #ff5b8a 49%, #ff5b8a 55%, #fba64b 77%, #f99b52)',
  'linear-gradient(43deg, rgb(65, 88, 208) 0%, rgb(200, 80, 192) 46%, rgb(255, 204, 112) 100%)',
  'linear-gradient(160deg, #0093E9 0%, #80D0C7 100%)',
  'linear-gradient(90deg, #00DBDE 0%, #FC00FF 100%)',
  'linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)',
  'linear-gradient(120deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(to right, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(to right, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(to right, #6a11cb 0%, #2575fc 100%)',
  'linear-gradient(to bottom, #6a11cb 0%, #2575fc 100%)',
  'linear-gradient(to top, #6a11cb 0%, #2575fc 100%)',
  'linear-gradient(to right bottom, rgb(42, 35, 86), rgb(62, 47, 107), rgb(90, 67, 163))',
];

GradientParser.prepareCssAngle = prepareCssAngle;
GradientParser.getRectAngleBound = getRectAngleBound;
GradientParser.create = createGradient;
GradientParser.is = isGradient;

export function createGradientFill(context, color, { x, y, w, h }) {
  if (GradientParser.is(color)) {
    return GradientParser.create(context, color, { x, y, w, h });
  } else {
    return color;
  }
}

function isGradient(fill) {
  if (!fill || typeof fill !== 'string') return false;
  const normalizedFill = fill.trim().toLowerCase();
  return (
    normalizedFill.startsWith('linear-gradient') ||
    normalizedFill.startsWith('radial-gradient') ||
    normalizedFill.startsWith('conic-gradient') ||
    normalizedFill.startsWith('repeating-linear-gradient') ||
    normalizedFill.startsWith('repeating-radial-gradient') ||
    normalizedFill.startsWith('repeating-conic-gradient')
  );
}

function renderGradient(context, color, { x, y, w, h }, render) {
  let gradient;
  if (!w || !h) return null;
  if (color.type === 'linear-gradient') {
    const angle = GradientParser.prepareCssAngle(color.orientation);
    // 使用整个宽度和高度计算边界点
    const startBound = GradientParser.getRectAngleBound(w, h, angle.revTheta);
    const endBound = GradientParser.getRectAngleBound(w, h, angle.theta);

    // 直接使用计算后的坐标，无需调整
    const start = { x: startBound.x, y: startBound.y };
    const end = { x: endBound.x, y: endBound.y };

    gradient = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y);
    const colorStops = color.colorStops;
    color.colorStops.forEach((color, index) => {
      let stop;
      if (color.length) {
        if (color.length.type === 'px') {
          // 计算像素相对于渐变长度的比例
          const distance = Math.hypot(end.x - start.x, end.y - start.y);
          stop = parseFloat(color.length.value) / distance;
        } else {
          stop = parseFloat(color.length.value) / 100;
        }
      } else {
        stop = colorStops.length === 2 ? (index === 0 ? 0 : 1) : index / (colorStops.length - 1);
      }
      let colorValue;
      if (color.type === 'hex') {
        colorValue = `#${color.value}`;
      } else if (color.type === 'rgb' || color.type === 'rgba') {
        colorValue = `rgba(${color.value.join(',')})`;
      }
      if (colorValue) {
        gradient.addColorStop(stop, colorValue);
      }
    });
  } else if (color.type === 'radial-gradient') {
    // Parse radial gradient shape and size from orientation
    const orientation = color.orientation
      ? color.orientation[0]
      : { type: 'shape', value: 'ellipse' };
    const isCircle = orientation?.type === 'shape' && orientation.value === 'circle';
    // Calculate gradient parameters based on shape
    const centerX = x + w / 2;
    const centerY = y + h / 2;
    const radius = isCircle ? Math.min(w, h) / 2 : Math.max(w, h) / 2;

    // Create radial gradient with inner and outer circles
    gradient = context.createRadialGradient(
      centerX,
      centerY,
      0, // Start circle (center)
      centerX,
      centerY,
      radius, // End circle
    );

    // Add color stops based on the gradient definition
    color.colorStops.forEach((color, index) => {
      // Calculate stop position
      let stop = 0;
      if (color.length) {
        if (color.length.type === 'px') {
          // Convert pixel values to percentage based on radius
          stop = parseFloat(color.length.value) / (radius * 2);
        } else {
          // Percentage values are used directly
          stop = parseFloat(color.length.value) / 100;
        }
      } else {
        // Default stops for first and last colors
        stop = index === 0 ? 0 : 1;
      }

      // Convert color to CSS format
      let colorValue;
      if (color.type === 'hex') {
        colorValue = `#${color.value}`;
      } else if (color.type === 'rgb' || color.type === 'rgba') {
        colorValue = `rgba(${color.value.join(',')})`;
      }

      // Add the color stop to the gradient
      if (colorValue) {
        gradient.addColorStop(stop, colorValue);
      }
    });
  } else if (color.type === 'repeating-linear-gradient') {
    // 处理重复线性渐变
    const angle = GradientParser.prepareCssAngle(color.orientation);

    // 使用整个宽度和高度计算边界点
    const startBound = GradientParser.getRectAngleBound(w, h, angle.revTheta);
    const endBound = GradientParser.getRectAngleBound(w, h, angle.theta);

    // 直接使用计算后的坐标
    const start = { x: startBound.x, y: startBound.y };
    const end = { x: endBound.x, y: endBound.y };

    gradient = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y);

    // 计算渐变图案的总长度
    let patternLength = 0;
    color.colorStops.forEach((stop) => {
      if (stop.length && stop.length.type === 'px') {
        patternLength = Math.max(patternLength, parseFloat(stop.length.value));
      }
    });

    // 计算需要重复的次数
    const distance = Math.hypot(end.x - start.x, end.y - start.y);
    const repetitions = Math.ceil(distance / patternLength);

    // 添加重复的色标
    const colorStops = color.colorStops;
    for (let i = 0; i < repetitions; i++) {
      colorStops.forEach((color, index) => {
        let stop = i / repetitions;
        if (color.length) {
          if (color.length.type === 'px') {
            stop += parseFloat(color.length.value) / distance / repetitions;
          } else {
            stop += parseFloat(color.length.value) / 100 / repetitions;
          }
        } else {
          stop += index / (colorStops.length - 1) / repetitions;
        }

        let colorValue;
        if (color.type === 'hex') {
          colorValue = `#${color.value}`;
        } else if (color.type === 'rgb' || color.type === 'rgba') {
          colorValue = `rgba(${color.value.join(',')})`;
        }

        if (colorValue) {
          gradient.addColorStop(Math.min(stop, 1), colorValue);
        }
      });
    }
  } else if (color.type === 'conic-gradient') {
    // Parse position from orientation
    const position = color.orientation?.position;
    let centerX = w * 0.5; // Default to center
    let centerY = h * 0.5;

    if (position) {
      // Handle percentage values for x and y
      if (position.x?.type === '%') {
        centerX = w * (parseFloat(position.x.value) / 100);
      }
      if (position.y?.type === '%') {
        centerY = h * (parseFloat(position.y.value) / 100);
      }
    }

    // Parse angle and create gradient
    let angle = 0;
    if (color.orientation?.value) {
      angle = parseFloat(color.orientation.value);
      // Normalize angle to be between 0 and 360
      angle = ((angle % 360) + 360) % 360;
      // Convert to radians and adjust 0° to point upward
      angle = (angle * Math.PI) / 180 - Math.PI / 2;
    }

    gradient = context.createConicGradient(angle, x + centerX, y + centerY);
    // Add color stops
    color.colorStops.forEach((stop) => {
      let position = 0;
      if (stop.length?.type === '%') {
        position = Math.max(0, Math.min(1, parseFloat(stop.length.value) / 100));
      }

      let colorValue;
      if (stop.type === 'hex') {
        colorValue = `#${stop.value}`;
      } else if (stop.type === 'rgb' || stop.type === 'rgba') {
        colorValue = `rgba(${stop.value.join(',')})`;
      }

      if (colorValue) {
        gradient.addColorStop(position, colorValue);
      }
    });
  } else {
    // console.warn('Unsupported gradient type:', color.type);
  }

  if (render) {
    context.fillStyle = gradient;
    render(gradient);
  }

  return gradient;
}

function createGradient(context, fill, size, render) {
  try {
    const colors = GradientParser.parse(fill);
    let pattern = null;
    colors.reverse().forEach((color) => {
      pattern = renderGradient(context, color, size, render);
    });
    return pattern;
  } catch (e) {
    console.error(e, fill);
  }
  return null;
}

function convertOrientationToRadians(orientation) {
  // 如果没有方向参数，默认为 180 度（向下）
  if (!orientation || !orientation.value) {
    return Math.PI;
  }

  const angle = orientation.value;

  // 处理关键字方向
  if (typeof angle === 'string') {
    const bottom = angle.indexOf('bottom') > -1;
    const top = angle.indexOf('top') > -1;
    const left = angle.indexOf('left') > -1;
    const right = angle.indexOf('right') > -1;

    if (bottom) {
      if (left) return 1.25 * Math.PI; // 225度
      if (right) return 0.75 * Math.PI; // 135度
      return Math.PI; // 180度
    }
    if (top) {
      if (left) return 1.75 * Math.PI; // 315度
      if (right) return 0.25 * Math.PI; // 45度
      return 0; // 0度
    }
    if (left) return 1.5 * Math.PI; // 270度
    if (right) return 0.5 * Math.PI; // 90度
  }

  // 处理数值角度
  const numericAngle = parseFloat(angle);

  // 转换不同单位到弧度
  if (orientation.type === 'grad') {
    return (numericAngle * Math.PI) / 200;
  } else if (orientation.type === 'rad') {
    return numericAngle;
  } else if (orientation.type === 'turn') {
    return numericAngle * 2 * Math.PI;
  } else {
    // 默认为 deg
    return (numericAngle * Math.PI) / 180;
  }
}

//takes the css angle and prepares it for the getRectAngleBound func
function prepareCssAngle(angle = 'to right') {
  let theta;
  let revTheta;

  // 修正theta的计算方式
  if (angle?.value) {
    theta = Math.PI / 2 - convertOrientationToRadians(angle);
  } else {
    theta = Math.PI / 2 - convertAngleToRadians(angle);
  }

  revTheta = theta + Math.PI;

  return {
    theta: theta,
    revTheta: revTheta,
  };
}

function convertAngleToRadians(angle) {
  let convertedAngle = parseFloat(angle);

  //check for CSS angle keywords
  //TODO: these values are currently wrong and need to be fixed
  if (angle.indexOf('to') > -1) {
    const bottom = angle.indexOf('bottom') > -1,
      top = angle.indexOf('top') > -1,
      left = angle.indexOf('left') > -1,
      right = angle.indexOf('right') > -1;

    if (bottom) {
      //bottom left, 225deg
      if (left) {
        convertedAngle = 225;
        //bottom right, 135deg
      } else if (right) {
        convertedAngle = 135;
        //bottom, 180deg
      } else {
        convertedAngle = 180;
      }
    } else if (top) {
      //top left, 315deg
      if (left) {
        convertedAngle = 315;
        //top right, 45deg
      } else if (right) {
        convertedAngle = 45;
        //top, no rotation
      } else {
        convertedAngle = 0;
      }
      //left, 270deg
    } else if (left) {
      convertedAngle = 270;
      //right, 90deg
    } else {
      convertedAngle = 90;
    }
    convertedAngle = (convertedAngle * Math.PI) / 180;
  } else if (angle.indexOf('deg') > -1) {
    convertedAngle = (convertedAngle * Math.PI) / 180;
  } else if (angle.indexOf('grad') > -1) {
    convertedAngle = (convertedAngle * Math.PI) / 200;
  } else if (angle.indexOf('turn') > -1) {
    convertedAngle = (convertedAngle * Math.PI) / 0.5;
  }
  //no value, 180deg
  else if (isNaN(convertedAngle)) {
    convertedAngle = Math.PI;
  }

  return convertedAngle;
}

//http://stackoverflow.com/questions/4061576/finding-points-on-a-rectangle-at-a-given-angle?noredirect=1&lq=1
//angle is in radians, starting position is the right side of the rectangle
//positive degrees move counter-clockwise, which is opposite of the css linear-gradient
function getRectAngleBound(rectWidth, rectHeight, angle) {
  const twoPI = Math.PI * 2;
  let theta = angle;

  while (theta < -Math.PI) {
    theta += twoPI;
  }

  while (theta > Math.PI) {
    theta -= twoPI;
  }

  const rectAtan = Math.atan2(rectHeight, rectWidth);
  const tanTheta = Math.tan(theta);
  let region;

  if (theta > -rectAtan && theta <= rectAtan) {
    region = 1;
  } else if (theta > rectAtan && theta <= Math.PI - rectAtan) {
    region = 2;
  } else if (theta > Math.PI - rectAtan || theta <= -(Math.PI - rectAtan)) {
    region = 3;
  } else {
    region = 4;
  }

  const edgePoint = { x: rectWidth / 2, y: rectHeight / 2 };
  const cx = rectWidth / 2;
  const cy = rectHeight / 2;

  switch (region) {
    case 1:
      //right
      edgePoint.x = rectWidth;
      edgePoint.y = cy - (rectWidth / 2) * tanTheta;
      break;
    case 2:
      //top
      edgePoint.x = cx + rectHeight / (2 * tanTheta);
      edgePoint.y = 0;
      break;
    case 3:
      //left
      edgePoint.x = 0;
      edgePoint.y = cy + (rectWidth / 2) * tanTheta;
      break;
    case 4:
      //bottom
      edgePoint.x = cx - rectHeight / (2 * tanTheta);
      edgePoint.y = rectHeight;
      break;
  }

  return {
    x: edgePoint.x,
    y: edgePoint.y,
    region: region,
  };
}

GradientParser.parse = (function () {
  const tokens = {
    linearGradient: /^(-(webkit|o|ms|moz)-)?(linear-gradient)/i,
    repeatingLinearGradient: /^(-(webkit|o|ms|moz)-)?(repeating-linear-gradient)/i,
    radialGradient: /^(-(webkit|o|ms|moz)-)?(radial-gradient)/i,
    repeatingRadialGradient: /^(-(webkit|o|ms|moz)-)?(repeating-radial-gradient)/i,
    conicGradient: /^(-(webkit|o|ms|moz)-)?(conic-gradient)/i,
    sideOrCorner:
      /^to (left (top|bottom)|right (top|bottom)|top (left|right)|bottom (left|right)|left|right|top|bottom)/i,
    extentKeywords: /^(closest-side|closest-corner|farthest-side|farthest-corner|contain|cover)/,
    positionKeywords: /^(left|center|right|top|bottom)/i,
    pixelValue: /^(-?(([0-9]*\.[0-9]+)|([0-9]+\.?)))px/,
    percentageValue: /^(-?(([0-9]*\.[0-9]+)|([0-9]+\.?)))%/,
    // Accept unitless zero for coordinates (e.g., "at 0 0")
    zeroValue: /^0(?=\s|,|\)|$)/,
    emValue: /^(-?(([0-9]*\.[0-9]+)|([0-9]+\.?)))em/,
    angleValue: /^(-?(([0-9]*\.[0-9]+)|([0-9]+\.?)))deg/,
    startCall: /^\(/,
    endCall: /^\)/,
    comma: /^,/,
    hexColor: /^#([0-9a-fA-F]+)/,
    literalColor: /^([a-zA-Z]+)/,
    rgbColor: /^rgb/i,
    rgbaColor: /^rgba/i,
    number: /^(([0-9]*\.[0-9]+)|([0-9]+\.?))/,
    from: /^from/i,
    at: /^at/i,
  };

  let input = '';

  function error(msg) {
    const err = new Error(input + ': ' + msg);
    err.source = input;
    throw err;
  }

  function getAST() {
    const ast = matchListDefinitions();

    if (input.length > 0) {
      error('Invalid input not EOF');
    }

    return ast;
  }

  function matchListDefinitions() {
    return matchListing(matchDefinition);
  }

  function matchDefinition() {
    return (
      matchGradient('linear-gradient', tokens.linearGradient, matchLinearOrientation) ||
      matchGradient(
        'repeating-linear-gradient',
        tokens.repeatingLinearGradient,
        matchLinearOrientation,
      ) ||
      matchGradient('radial-gradient', tokens.radialGradient, matchListRadialOrientations) ||
      matchGradient(
        'repeating-radial-gradient',
        tokens.repeatingRadialGradient,
        matchListRadialOrientations,
      ) ||
      matchGradient('conic-gradient', tokens.conicGradient, matchConicOrientation)
    );
  }

  function matchGradient(gradientType, pattern, orientationMatcher) {
    return matchCall(pattern, function () {
      const orientation = orientationMatcher();
      if (orientation) {
        if (!scan(tokens.comma)) {
          error('Missing comma before color stops');
        }
      }

      const colorStops = matchListing(matchColorStop);

      // Add default length values if not specified
      addDefaultLengthsToColorStops(colorStops);

      return {
        type: gradientType,
        orientation: orientation,
        colorStops: colorStops,
      };
    });
  }

  function addDefaultLengthsToColorStops(colorStops) {
    if (!colorStops || colorStops.length === 0) return;

    // Check if any colorStop is missing a length
    const needsDefaultLengths = colorStops.some(function (stop) {
      return !stop.length;
    });

    if (needsDefaultLengths) {
      // Apply default percentage lengths evenly distributed
      for (let i = 0; i < colorStops.length; i++) {
        if (!colorStops[i].length) {
          var percentage;
          if (colorStops.length === 1) {
            percentage = '50';
          } else {
            percentage = ((i / (colorStops.length - 1)) * 100).toString();
          }

          colorStops[i].length = {
            type: '%',
            value: percentage,
          };
        }
      }
    }
  }

  function matchCall(pattern, callback) {
    const captures = scan(pattern);

    if (captures) {
      if (!scan(tokens.startCall)) {
        error('Missing (');
      }

      const result = callback(captures);

      if (!scan(tokens.endCall)) {
        error('Missing )');
      }

      return result;
    }
  }

  function matchConicOrientation() {
    let angle;
    let position = null;

    // Check for "from" angle
    if (scan(tokens.from)) {
      angle = matchAngle();
      if (!angle) {
        error('Expected angle after "from"');
      }
    } else {
      angle = matchAngle();
    }

    // Check for "at" position
    if (scan(tokens.at)) {
      position = matchCoordinates();
      if (!position.x && !position.y) {
        error('Expected coordinates after "at"');
      }
    }

    if (angle || position) {
      return {
        type: 'conic',
        value: angle ? angle.value : 0,
        position: position,
      };
    }
    return null;
  }

  function matchLinearOrientation() {
    return matchSideOrCorner() || matchAngle();
  }

  function matchSideOrCorner() {
    return match('directional', tokens.sideOrCorner, 1);
  }

  function matchAngle() {
    return match('angular', tokens.angleValue, 1);
  }

  function matchListRadialOrientations() {
    let radialOrientations,
      radialOrientation = matchRadialOrientation(),
      lookaheadCache;

    if (radialOrientation) {
      radialOrientations = [];
      radialOrientations.push(radialOrientation);

      lookaheadCache = input;
      if (scan(tokens.comma)) {
        radialOrientation = matchRadialOrientation();
        if (radialOrientation) {
          radialOrientations.push(radialOrientation);
        } else {
          input = lookaheadCache;
        }
      }
    }

    return radialOrientations;
  }

  function matchRadialOrientation() {
    let radialType = matchCircle() || matchEllipse();

    if (radialType) {
      radialType.at = matchAtPosition();
    } else {
      const extent = matchExtentKeyword();
      if (extent) {
        radialType = extent;
        const positionAt = matchAtPosition();
        if (positionAt) {
          radialType.at = positionAt;
        }
      } else {
        const defaultPosition = matchPositioning();
        if (defaultPosition) {
          radialType = {
            type: 'default-radial',
            at: defaultPosition,
          };
        }
      }
    }

    return radialType;
  }

  function matchCircle() {
    const circle = match('shape', /^(circle)/i, 0);

    if (circle) {
      circle.style = matchLength() || matchExtentKeyword();
    }

    return circle;
  }

  function matchEllipse() {
    const ellipse = match('shape', /^(ellipse)/i, 0);

    if (ellipse) {
      ellipse.style = matchDistance() || matchExtentKeyword();
    }

    return ellipse;
  }

  function matchExtentKeyword() {
    return match('extent-keyword', tokens.extentKeywords, 1);
  }

  function matchAtPosition() {
    if (match('position', /^at/, 0)) {
      const positioning = matchPositioning();

      if (!positioning) {
        error('Missing positioning value');
      }

      return positioning;
    }
  }

  function matchPositioning() {
    const location = matchCoordinates();

    if (location.x || location.y) {
      return {
        type: 'position',
        value: location,
      };
    }
  }

  function matchCoordinates() {
    return {
      x: matchDistance(),
      y: matchDistance(),
    };
  }

  function matchListing(matcher) {
    let captures = matcher(),
      result = [];

    if (captures) {
      result.push(captures);
      while (scan(tokens.comma)) {
        captures = matcher();
        if (captures) {
          result.push(captures);
        } else {
          error('One extra comma');
        }
      }
    }

    return result;
  }

  function matchColorStop() {
    const color = matchColor();

    if (!color) {
      error('Expected color definition');
    }

    color.length = matchDistance();
    return color;
  }

  function matchColor() {
    return matchHexColor() || matchRGBAColor() || matchRGBColor() || matchLiteralColor();
  }

  function matchLiteralColor() {
    return match('literal', tokens.literalColor, 0);
  }

  function matchHexColor() {
    return match('hex', tokens.hexColor, 1);
  }

  function matchRGBColor() {
    return matchCall(tokens.rgbColor, function () {
      return {
        type: 'rgb',
        value: matchListing(matchNumber),
      };
    });
  }

  function matchRGBAColor() {
    return matchCall(tokens.rgbaColor, function () {
      return {
        type: 'rgba',
        value: matchListing(matchNumber),
      };
    });
  }

  function matchNumber() {
    return scan(tokens.number)[1];
  }

  function matchDistance() {
    return (
      match('%', tokens.percentageValue, 1) ||
      matchPositionKeyword() ||
      matchLength() ||
      matchZero()
    );
  }

  function matchPositionKeyword() {
    return match('position-keyword', tokens.positionKeywords, 1);
  }

  function matchLength() {
    return match('px', tokens.pixelValue, 1) || match('em', tokens.emValue, 1);
  }

  // Treat unitless zero as 0% for positioning contexts (e.g., "at 0 0")
  function matchZero() {
    const captures = scan(tokens.zeroValue);
    if (captures) {
      return {
        type: '%',
        value: '0',
      };
    }
  }

  function match(type, pattern, captureIndex) {
    const captures = scan(pattern);
    if (captures) {
      return {
        type: type,
        value: captures[captureIndex],
      };
    }
  }

  function scan(regexp) {
    let captures, blankCaptures;

    blankCaptures = /^[\n\r\t\s]+/.exec(input);
    if (blankCaptures) {
      consume(blankCaptures[0].length);
    }

    captures = regexp.exec(input);
    if (captures) {
      consume(captures[0].length);
    }

    return captures;
  }

  function consume(size) {
    input = input.substr(size);
  }

  return function (code) {
    input = code.toString();
    return getAST();
  };
})();

GradientParser.stringify = (function () {
  var visitor = {
    'visit_linear-gradient': function (node) {
      return visitor.visit_gradient(node);
    },

    'visit_repeating-linear-gradient': function (node) {
      return visitor.visit_gradient(node);
    },

    'visit_radial-gradient': function (node) {
      return visitor.visit_gradient(node);
    },

    'visit_repeating-radial-gradient': function (node) {
      return visitor.visit_gradient(node);
    },

    visit_gradient: function (node) {
      let orientation = visitor.visit(node.orientation);
      if (orientation) {
        orientation += ', ';
      }

      return node.type + '(' + orientation + visitor.visit(node.colorStops) + ')';
    },

    visit_shape: function (node) {
      let result = node.value,
        at = visitor.visit(node.at),
        style = visitor.visit(node.style);

      if (style) {
        result += ' ' + style;
      }

      if (at) {
        result += ' at ' + at;
      }

      return result;
    },

    'visit_default-radial': function (node) {
      let result = '',
        at = visitor.visit(node.at);

      if (at) {
        result += at;
      }
      return result;
    },

    'visit_extent-keyword': function (node) {
      let result = node.value,
        at = visitor.visit(node.at);

      if (at) {
        result += ' at ' + at;
      }

      return result;
    },

    'visit_position-keyword': function (node) {
      return node.value;
    },

    visit_position: function (node) {
      return visitor.visit(node.value.x) + ' ' + visitor.visit(node.value.y);
    },

    'visit_%': function (node) {
      return node.value + '%';
    },

    visit_em: function (node) {
      return node.value + 'em';
    },

    visit_px: function (node) {
      return node.value + 'px';
    },

    visit_literal: function (node) {
      return visitor.visit_color(node.value, node);
    },

    visit_hex: function (node) {
      return visitor.visit_color('#' + node.value, node);
    },

    visit_rgb: function (node) {
      return visitor.visit_color('rgb(' + node.value.join(', ') + ')', node);
    },

    visit_rgba: function (node) {
      return visitor.visit_color('rgba(' + node.value.join(', ') + ')', node);
    },

    visit_color: function (resultColor, node) {
      let result = resultColor,
        length = visitor.visit(node.length);

      if (length) {
        result += ' ' + length;
      }
      return result;
    },

    visit_angular: function (node) {
      return node.value + 'deg';
    },

    visit_directional: function (node) {
      return 'to ' + node.value;
    },

    visit_array: function (elements) {
      let result = '',
        size = elements.length;

      elements.forEach(function (element, i) {
        result += visitor.visit(element);
        if (i < size - 1) {
          result += ', ';
        }
      });

      return result;
    },

    visit: function (element) {
      if (!element) {
        return '';
      }
      const result = '';

      if (element instanceof Array) {
        return visitor.visit_array(element, result);
      } else if (element.type) {
        const nodeVisitor = visitor['visit_' + element.type];
        if (nodeVisitor) {
          return nodeVisitor(element);
        } else {
          throw Error('Missing visitor visit_' + element.type);
        }
      } else {
        throw Error('Invalid node.');
      }
    },
  };

  return function (root) {
    return visitor.visit(root);
  };
})();
